import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import { useState, useEffect, useCallback } from "react";
import { fetchListingsWithPredictions, authFetch } from "../api";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";

const mapContainerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: 40.7167, lng: -73.956 }; // Williamsburg

export default function MapPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const [allListings, setAllListings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 30;

  // Filter states
  const [searchAddress, setSearchAddress] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bedsMin, setBedsMin] = useState("");
  const [bedsMax, setBedsMax] = useState("");
  const [bathsMin, setBathsMin] = useState("");
  const [bathsMax, setBathsMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [capRateMin, setCapRateMin] = useState("");
  const [capRateMax, setCapRateMax] = useState("");
  const [daysOnMarketMax, setDaysOnMarketMax] = useState("");
  const [propertyType, setPropertyType] = useState("all"); // "all", "condo", "coop"

  // Neighborhood filter states
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]);
  const [neighborhoodDropdownOpen, setNeighborhoodDropdownOpen] = useState(false);
  const [neighborhoodsLoaded, setNeighborhoodsLoaded] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  // Load neighborhoods on mount
  const loadNeighborhoods = useCallback(async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setNeighborhoods(data.neighborhoods || []);

      // Convert default_neighborhoods to IDs
      let defaultIds = [];
      if (data.default_neighborhoods && data.default_neighborhoods.length > 0) {
        defaultIds = data.default_neighborhoods.map(item => {
          if (typeof item === 'number') return item;
          if (typeof item === 'string' && /^\d+$/.test(item)) return parseInt(item, 10);
          const found = data.neighborhoods.find(n => n.name === item);
          return found ? found.id : null;
        }).filter(id => id !== null);
      }

      setDefaultNeighborhoods(defaultIds);
      if (defaultIds.length > 0) {
        setSelectedNeighborhoods(defaultIds);
      }
      setNeighborhoodsLoaded(true);
    } catch (error) {
      console.error("Error loading neighborhoods:", error);
      setNeighborhoodsLoaded(true); // Still mark as loaded so we can proceed
    }
  }, []);

  // Fetch listings with filters
  const fetchData = useCallback(() => {
    setLoading(true);
    const filters = {};
    if (searchAddress) filters.search = searchAddress;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (bedsMin) filters.beds_min = bedsMin;
    if (bedsMax) filters.beds_max = bedsMax;
    if (bathsMin) filters.baths_min = bathsMin;
    if (bathsMax) filters.baths_max = bathsMax;
    if (priceMin) filters.price_min = priceMin;
    if (priceMax) filters.price_max = priceMax;
    if (capRateMin) filters.cap_rate_min = capRateMin;
    if (capRateMax) filters.cap_rate_max = capRateMax;
    if (daysOnMarketMax) filters.days_on_market_max = daysOnMarketMax;
    if (propertyType && propertyType !== "all") filters.property_type = propertyType;
    if (selectedNeighborhoods.length > 0) filters.neighborhood_ids = selectedNeighborhoods.join(",");

    fetchListingsWithPredictions(filters)
      .then((data) => {
        if (!data || !Array.isArray(data.listings)) {
          throw new Error("Invalid response: listings is not an array");
        }
        setAllListings(data.listings);
        setTotalCount(data.total || data.listings.length);

        // Center map on listings if we have any
        if (data.listings.length > 0) {
          const validListings = data.listings.filter(l => l.latitude && l.longitude);
          if (validListings.length > 0) {
            const avgLat = validListings.reduce((sum, l) => sum + l.latitude, 0) / validListings.length;
            const avgLng = validListings.reduce((sum, l) => sum + l.longitude, 0) / validListings.length;
            setMapCenter({ lat: avgLat, lng: avgLng });
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching listings:", err);
        setError(err.message || "Unknown error");
      })
      .finally(() => setLoading(false));
  }, [searchAddress, dateFrom, dateTo, bedsMin, bedsMax, bathsMin, bathsMax, priceMin, priceMax, capRateMin, capRateMax, daysOnMarketMax, propertyType, selectedNeighborhoods]);

  // Load neighborhoods on mount
  useEffect(() => {
    loadNeighborhoods();
  }, [loadNeighborhoods]);

  // Fetch listings when neighborhoods are loaded and selected neighborhoods change
  useEffect(() => {
    if (neighborhoodsLoaded) {
      fetchData();
    }
  }, [neighborhoodsLoaded, selectedNeighborhoods, fetchData]);

  // Handle search button click
  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchAddress("");
    setDateFrom("");
    setDateTo("");
    setBedsMin("");
    setBedsMax("");
    setBathsMin("");
    setBathsMax("");
    setPriceMin("");
    setPriceMax("");
    setCapRateMin("");
    setCapRateMax("");
    setDaysOnMarketMax("");
    setPropertyType("all");
    setSelectedNeighborhoods(defaultNeighborhoods.length > 0 ? defaultNeighborhoods : []);
    setPage(1);
  };

  // Paginate listings for table (already filtered by backend)
  const totalPages = Math.ceil(allListings.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedListings = allListings.slice(startIndex, endIndex);

  // Show only current page listings on map
  const mapListings = paginatedListings;

  const pageContainerStyle = {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: "#f5f5f5",
  };

  const contentStyle = {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  };

  const mapContainerWrapperStyle = {
    flex: 1,
    position: "relative",
  };

  const tableContainerStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderLeft: "1px solid #e0e0e0",
  };

  const tableWrapperStyle = {
    flex: 1,
    overflowY: "auto",
  };

  const paginationContainerStyle = {
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderTop: "1px solid #dee2e6",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const paginationButtonStyle = {
    backgroundColor: "#000",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  };

  const disabledButtonStyle = {
    ...paginationButtonStyle,
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  };

  const tableHeaderStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#f8f9fa",
    borderBottom: "2px solid #dee2e6",
    zIndex: 10,
  };

  const thStyle = {
    padding: "12px 8px",
    textAlign: "left",
    fontWeight: "600",
    color: "#495057",
    fontSize: "12px",
    textTransform: "uppercase",
  };

  const tdStyle = {
    padding: "10px 8px",
    borderBottom: "1px solid #e9ecef",
  };

  const rowStyle = (id) => ({
    cursor: "pointer",
    backgroundColor: hoveredId === id || selected?.id === id ? "#e3f2fd" : "white",
    transition: "background-color 0.2s ease",
  });

  const linkStyle = {
    color: "#007bff",
    textDecoration: "none",
    marginRight: "8px",
    fontSize: "12px",
  };

  const externalLinkStyle = {
    color: "#28a745",
    textDecoration: "none",
    fontSize: "12px",
  };

  // Format price change for display (compact for table)
  const formatPriceChange = (change, pct) => {
    if (!change || change === 0) return null;
    const sign = change > 0 ? "+" : "-";
    const absChange = Math.abs(change);
    const formattedChange = absChange >= 1000
      ? `${sign}$${(absChange / 1000).toFixed(0)}K`
      : `${sign}$${absChange.toLocaleString()}`;
    const formattedPct = pct ? ` (${sign}${Math.abs(pct).toFixed(1)}%)` : "";
    return `${formattedChange}${formattedPct}`;
  };

  // Get color for price change
  const getPriceChangeColor = (priceChange) => {
    if (priceChange && priceChange < 0) return "#2e7d32"; // green for drop
    if (priceChange && priceChange > 0) return "#d32f2f"; // red for increase
    return "#666"; // default gray
  };

  const infoWindowStyle = {
    maxWidth: "250px",
  };

  const propertyInfoStyle = {
    margin: "8px 0",
    padding: 0,
    listStyle: "none",
  };

  const propertyInfoItemStyle = {
    margin: "4px 0",
    fontSize: "14px",
    fontWeight: "500",
  };

  const viewLinkStyle = {
    display: "inline-block",
    backgroundColor: "#000",
    color: "white",
    padding: "6px 12px",
    borderRadius: "4px",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: "600",
    marginTop: "8px",
    transition: "background-color 0.3s ease",
  };

  if (!isLoaded) return (
    <div style={pageContainerStyle}>
      <Header showLogout={true} />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>Loading Map...</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={pageContainerStyle}>
      <Header showLogout={true} />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>Loading listings...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={pageContainerStyle}>
      <Header showLogout={true} />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "18px", color: "#dc3545" }}>Error: {error}</div>
      </div>
    </div>
  );

  return (
    <div style={pageContainerStyle}>
      <Header showLogout={true} />

      <div style={contentStyle}>
        {/* Map Section - Left 50% */}
        <div style={mapContainerWrapperStyle}>
          <GoogleMap
            key={`map-${mapListings.length}-${mapCenter.lat}-${mapCenter.lng}`}
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={14}
          >
            {mapListings.map((listing) => (
              <Marker
                key={listing.id}
                position={{ lat: listing.latitude, lng: listing.longitude }}
                onClick={() => setSelected(listing)}
                icon={
                  hoveredId === listing.id || selected?.id === listing.id
                    ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: "#007bff",
                        fillOpacity: 1,
                        strokeColor: "white",
                        strokeWeight: 2,
                      }
                    : undefined
                }
              />
            ))}

            {selected && (
              <InfoWindow
                position={{ lat: selected.latitude, lng: selected.longitude }}
                onCloseClick={() => setSelected(null)}
              >
                <div style={infoWindowStyle}>
                  <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600" }}>
                    {selected.address}
                  </h4>

                  <ul style={propertyInfoStyle}>
                    <li style={propertyInfoItemStyle}>
                      {selected.bedrooms} BR / {selected.bathrooms} Bath
                    </li>
                    {selected.sqft && (
                      <li style={propertyInfoItemStyle}>
                        {selected.sqft.toLocaleString()} sqft
                      </li>
                    )}
                    {selected.asking_price && (
                      <li style={propertyInfoItemStyle}>
                        Price: {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        }).format(selected.asking_price)}
                      </li>
                    )}
                    {selected.predicted_rent && (
                      <li style={propertyInfoItemStyle}>
                        Rent: ${selected.predicted_rent.toLocaleString()}/mo
                      </li>
                    )}
                    {selected.net_yield && (
                      <li style={propertyInfoItemStyle}>
                        Cap Rate: {selected.net_yield.toFixed(2)}%
                        {selected.neighborhood_avg_cap_rate && (
                          <span style={{ fontSize: "12px", color: "#666", marginLeft: "6px" }}>
                            (Neighborhood: {selected.neighborhood_avg_cap_rate.toFixed(2)}%)
                          </span>
                        )}
                      </li>
                    )}
                  </ul>

                  <Link
                    to={`/predict/${selected.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={viewLinkStyle}
                    onMouseEnter={(e) => {e.target.style.backgroundColor = "#333"}}
                    onMouseLeave={(e) => {e.target.style.backgroundColor = "#000"}}
                  >
                    View Details
                  </Link>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {/* Table Section - Right 50% */}
        <div style={tableContainerStyle}>
          {/* Filter Panel */}
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6"
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "12px",
              marginBottom: "12px"
            }}>
              {/* Neighborhoods */}
              <div style={{ gridColumn: "1 / -1", position: "relative" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Neighborhoods
                </label>
                <div
                  onClick={() => setNeighborhoodDropdownOpen(!neighborhoodDropdownOpen)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px",
                    backgroundColor: selectedNeighborhoods.length > 0 ? "#e3f2fd" : "white",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minHeight: "32px",
                    boxSizing: "border-box"
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {selectedNeighborhoods.length === 0
                      ? "All Neighborhoods"
                      : selectedNeighborhoods.length === 1
                      ? neighborhoods.find(n => n.id === selectedNeighborhoods[0])?.name || "1 neighborhood"
                      : `${selectedNeighborhoods.length} neighborhoods selected`}
                  </span>
                  <span style={{ marginLeft: "8px", color: "#666" }}>{neighborhoodDropdownOpen ? "▲" : "▼"}</span>
                </div>
                {neighborhoodDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      maxHeight: "250px",
                      overflowY: "auto"
                    }}
                  >
                    <div style={{ padding: "8px", borderBottom: "1px solid #eee", display: "flex", gap: "8px" }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedNeighborhoods([]); }}
                        style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#f5f5f5", border: "1px solid #ddd", borderRadius: "3px", cursor: "pointer" }}
                      >
                        Clear All
                      </button>
                      {defaultNeighborhoods.length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedNeighborhoods(defaultNeighborhoods); }}
                          style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "3px", cursor: "pointer" }}
                        >
                          My Defaults ({defaultNeighborhoods.length})
                        </button>
                      )}
                    </div>
                    {[...neighborhoods].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
                      <label
                        key={n.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 12px",
                          cursor: "pointer",
                          backgroundColor: selectedNeighborhoods.includes(n.id) ? "#e3f2fd" : "transparent",
                          borderBottom: "1px solid #f0f0f0"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedNeighborhoods.includes(n.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedNeighborhoods([...selectedNeighborhoods, n.id]);
                            } else {
                              setSelectedNeighborhoods(selectedNeighborhoods.filter(x => x !== n.id));
                            }
                          }}
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ flex: 1 }}>{n.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {neighborhoodDropdownOpen && (
                  <div
                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                    onClick={() => setNeighborhoodDropdownOpen(false)}
                  />
                )}
              </div>

              {/* Search */}
              <div style={{ gridColumn: "1 / -1" }}>
                <input
                  type="text"
                  placeholder="Search address..."
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
              </div>

              {/* Property Type */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Property Type
                </label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px",
                    backgroundColor: "white"
                  }}
                >
                  <option value="all">All Properties</option>
                  <option value="condo">Condo</option>
                  <option value="coop">Co-op</option>
                </select>
              </div>

              {/* Bedrooms */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Bedrooms
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={bedsMin}
                    onChange={(e) => setBedsMin(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={bedsMax}
                    onChange={(e) => setBedsMax(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                </div>
              </div>

              {/* Bathrooms */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Bathrooms
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="number"
                    step="0.5"
                    placeholder="Min"
                    value={bathsMin}
                    onChange={(e) => setBathsMin(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                  <input
                    type="number"
                    step="0.5"
                    placeholder="Max"
                    value={bathsMax}
                    onChange={(e) => setBathsMax(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Asking Price ($)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                </div>
              </div>

              {/* Cap Rate */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Cap Rate (%)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    value={capRateMin}
                    onChange={(e) => setCapRateMin(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px",
                      backgroundColor: "white"
                    }}
                  >
                    <option value="">From</option>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                  <select
                    value={capRateMax}
                    onChange={(e) => setCapRateMax(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px",
                      backgroundColor: "white"
                    }}
                  >
                    <option value="">To</option>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                      <option key={v} value={v}>{v}%</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Days on Market */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Days on Market
                </label>
                <input
                  type="number"
                  placeholder="Max days"
                  min="0"
                  value={daysOnMarketMax}
                  onChange={(e) => setDaysOnMarketMax(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
              </div>

              {/* Date Listed */}
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                  Date Listed
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={handleSearch}
                style={{
                  padding: "8px 24px",
                  backgroundColor: "#000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                Search
              </button>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer"
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead style={tableHeaderStyle}>
                <tr>
                  <th style={thStyle}>Address</th>
                  <th style={thStyle}>Bed/Bath</th>
                  <th style={thStyle}>Asking Price</th>
                  <th style={thStyle}>Date Listed</th>
                  <th style={thStyle}>Pred. Rent</th>
                  <th style={thStyle}>Cap Rate %</th>
                  <th style={thStyle}>Links</th>
                </tr>
              </thead>
              <tbody>
                {paginatedListings.map((listing) => (
                <tr
                  key={listing.id}
                  style={rowStyle(listing.id)}
                  onMouseEnter={() => setHoveredId(listing.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => setSelected(listing)}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                      {listing.address}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6c757d" }}>
                      {listing.neighborhood}, {listing.borough}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {listing.bedrooms} / {listing.bathrooms}
                  </td>
                  <td style={tdStyle}>
                    <div>
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      }).format(listing.asking_price)}
                      {formatPriceChange(listing.price_change, listing.price_change_pct) && (
                        <div style={{ fontSize: "10px", color: getPriceChangeColor(listing.price_change), marginTop: "2px" }}>
                          {formatPriceChange(listing.price_change, listing.price_change_pct)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {listing.listed_at
                      ? new Date(listing.listed_at).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td style={tdStyle}>
                    ${listing.predicted_rent.toLocaleString()}/mo
                  </td>
                  <td style={tdStyle}>
                    <div>
                      <span style={{ fontWeight: "600", color: listing.net_yield > 5 ? "#28a745" : listing.net_yield > 3 ? "#ffc107" : "#dc3545" }}>
                        {listing.net_yield.toFixed(2)}%
                      </span>
                      {listing.neighborhood_avg_cap_rate && (
                        <div style={{ fontSize: "10px", color: "#6c757d", marginTop: "2px" }}>
                          Neighborhood: {listing.neighborhood_avg_cap_rate.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <Link to={`/predict/${listing.id}`} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                      Details
                    </Link>
                    {listing.listing_url && (
                      <a
                        href={listing.listing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={externalLinkStyle}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Web Listing
                      </a>
                    )}
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div style={paginationContainerStyle}>
            <span style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
              Page {page} of {totalPages} ({totalCount} properties)
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                style={page === 1 ? disabledButtonStyle : paginationButtonStyle}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                style={page === totalPages ? disabledButtonStyle : paginationButtonStyle}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
